import { isCurved, isFolded } from '@signa/shared';
import { curvedCellAt, curvedLayout, curvedOutline, largestPolygon } from './curve.js';
import { frameLayout } from './fit.js';
import { faceDraws, foldedLayout } from './folds.js';
import { apply, invert, rectToQuad, DegenerateQuadError } from './homography.js';
/**
 * 点がどのセルにあるか(#152)。
 *
 * ## なぜ描画コアに置くか
 *
 * **セルの位置はホモグラフィの逆写像がないと出せない。** 盤面は写真の遠近に
 * 合わせて台形になっているので、「クアッドを縦に半分」では**セルの境目に
 * ならない** —— 遠い側のセルのほうが画面上では狭い。
 *
 * 呼び出し側(S04・ビューア)に同じ計算を持たせると、**描画とドロップ判定で
 * 別々の答えが出る。** 描画コア側に 1 つだけ置く([ADR-0029](../../../docs/adr/0029-panel-split-grid.md))。
 *
 * ## 座標系はどれでもよい
 *
 * **`point` と `quad` が同じ座標系なら、正規化でも表示座標でもよい。**
 * 盤面の矩形は分割の比率(`grid`)から作られ、クアッドとの対応は射影変換で
 * 決まる —— **座標系の一様な拡大は答えを変えない。**
 *
 * だから S04 は正規化座標のまま判定でき、`DisplayTransform` を渡さずに済む。
 *
 * **曲面の枠は例外 —— 正規化座標で呼ぶ**(#361 / [ADR-0060](../../../docs/adr/0060-curved-surface.md) の決定 7)。
 * 曲面の形は `quad` ではなく保存した形(`curve.shape`)から出るので、呼び出し側の座標系に合わせられない ——
 * 形は**正規化座標**で返し、判定もそこで行う。キャンバスの縦横比は使わない(`shape.aspect` で写す —— [`curve.ts`](./curve.ts))。
 *
 * **純粋関数。** WebGL に触らないので検査できる([ADR-0019](../../../docs/adr/0019-dev-foundation.md))。
 */
/**
 * 点の入っているセルの番号(行優先)。
 *
 * 盤面の外・潰れたクアッドなら `null`。**分割が無ければ、内側なら必ず `0`。**
 *
 * 境界線の真上は**左・上のセル**に入れる(行優先の走査で先に当たるほう)。
 * どちらに入っても実害は無いが、**決まっていないと同じ操作で結果が揺れる。**
 */
export function cellAt(point, quad, grid, 
/**
 * **折れ目**(#339 / [ADR-0057](../../../docs/adr/0057-folded-frame.md))。**省略可** —— 平面の枠として判定する。
 *
 * 折れた枠は**面のどれかの内側ならセル 0**(入れ物は 1 つ —— 決定 4)、外なら `null`。
 * 角は外側の 4 隅の線より張り出したり、へこんだりするので、**面ごとに逆写像で確かめる**。
 */
folds, 
/**
 * **曲面**(#361 / [ADR-0060](../../../docs/adr/0060-curved-surface.md))。**省略可** —— 平面か折れた枠として判定する。
 *
 * 曲面の枠は、点を含む片のうち**カメラにいちばん近いもの**のセル。**それが裏なら `null`**(手前の裏に隠れた・裏に回った ——
 * 素材を落とせない)。**`point` は正規化座標**(上の説明)。`quad` は使わない。折れ目と両方あれば曲面を優先する。
 */
curve) {
    const curved = { curve };
    if (isCurved(curved)) {
        const layout = curvedLayout(curved.curve, grid);
        return layout === null ? null : curvedCellAt(layout, point);
    }
    if (folds != null && isFolded({ folds }))
        return foldedCellAt(point, quad, grid, folds);
    const layout = frameLayout(quad, grid);
    if (layout === null)
        return null;
    let inverse;
    try {
        inverse = invert(rectToQuad(layout.panel.width, layout.panel.height, quad));
    }
    catch (error) {
        // 頂点が潰れている間は判定しない(編集中に一時的に起こりうる)。
        if (error instanceof DegenerateQuadError)
            return null;
        throw error;
    }
    const [x, y] = apply(inverse, point);
    if (!Number.isFinite(x) || !Number.isFinite(y))
        return null;
    /*
     * **縁には余裕を持たせる。** 呼び出し側は先にクアッドの内包判定を通して
     * いることが多く、そこで「内側」と出た点が逆写像の丸めでわずかに外へ
     * 出ることがある —— そこで弾くと**枠の縁に落とした操作が失敗する。**
     */
    const tolerance = Math.max(layout.panel.width, layout.panel.height) * 1e-6;
    if (x < -tolerance || x > layout.panel.width + tolerance)
        return null;
    if (y < -tolerance || y > layout.panel.height + tolerance)
        return null;
    for (const cell of layout.cells) {
        const [left, top] = cell.corners[0];
        const [right, bottom] = cell.corners[2];
        if (x >= left - tolerance && x <= right + tolerance) {
            if (y >= top - tolerance && y <= bottom + tolerance)
                return cell.index;
        }
    }
    return null;
}
/**
 * セルの輪郭を `quad` と同じ座標系で返す(#152)。**左上から時計回り。**
 *
 * **ドロップ先を光らせるのに使う。** 分割した盤面では枠全体を光らせても
 * 「どのセルに入るか」が分からない —— [`cellAt`](#cellAt) が返した番号を
 * そのまま渡す。
 *
 * 台形の盤面ではセルの輪郭も台形になる。**画面上で等分にはならない** ——
 * 遠い側のセルほど狭く見えるのが正しい。
 *
 * 範囲外の番号・潰れたクアッドなら `null`。
 */
export function cellOutline(quad, grid, cellIndex, 
/**
 * **折れ目**(#339)。**省略可** —— 平面の枠として扱う。
 *
 * 折れた枠は入れ物が 1 つなので、**セル 0 の輪郭 = 枠全体**(面をつないだ多角形 —— ADR-0057 の決定 4)。
 * それ以外の番号は `null`(連動先の寄せ —— `linkedCells` —— を通せば 0 しか来ない)。
 */
folds, 
/**
 * **曲面**(#361)。**省略可。** 曲面の枠は**セルの見えている部分の輪郭**(表の片の外周 —— 2 つに分かれていれば大きいほう)を
 * **正規化座標**で返す。**裏に回って見えないセルは `null`**(案内も強調も出さない)。内向きの手前の裏に隠れる部分は含む。
 */
curve) {
    const curved = { curve };
    if (isCurved(curved)) {
        const layout = curvedLayout(curved.curve, grid);
        return layout === null ? null : largestPolygon(curvedOutline(layout, cellIndex).polygons);
    }
    if (folds != null && isFolded({ folds })) {
        return cellIndex === 0 ? (foldedLayout(quad, grid, folds)?.outline ?? null) : null;
    }
    const layout = frameLayout(quad, grid);
    const cell = layout?.cells.find((c) => c.index === cellIndex);
    if (layout === null || cell === undefined)
        return null;
    try {
        const toQuad = rectToQuad(layout.panel.width, layout.panel.height, quad);
        return cell.corners.map((corner) => apply(toQuad, corner));
    }
    catch (error) {
        if (error instanceof DegenerateQuadError)
            return null;
        throw error;
    }
}
/**
 * 折れた枠の当たり判定。**面のどれかの内側ならセル 0、外なら `null`。**
 *
 * 面ごとに逆写像で展開図の座標へ戻し、その面の範囲に入るかを見る。縁の余裕は平面の枠と同じ
 * (呼び出し側の内包判定で「内側」と出た点が、丸めでわずかに外へ出ても弾かない)。
 */
function foldedCellAt(point, quad, grid, folds) {
    const layout = foldedLayout(quad, grid, folds);
    if (layout === null)
        return null;
    const tolerance = Math.max(layout.panel.width, layout.panel.height) * 1e-6;
    for (const face of faceDraws(layout)) {
        const [x, y] = apply(face.inverse, point);
        if (!Number.isFinite(x) || !Number.isFinite(y))
            continue;
        const [left, top] = face.panelCorners[0];
        const [right, bottom] = face.panelCorners[2];
        if (x >= left - tolerance && x <= right + tolerance) {
            if (y >= top - tolerance && y <= bottom + tolerance)
                return 0;
        }
    }
    return null;
}
//# sourceMappingURL=hit-test.js.map